"use client";

import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Input } from "../../ui/input";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart as RechartsLineChart,
  type TooltipProps,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AlertCircle, ArrowLeft, CheckCircle, ChevronDown, LineChart, Loader2, Play, Save, Upload } from "lucide-react";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import {
  getProcedureFileUsageForWorkspace,
  updateProcedureFileUsageForWorkspace,
  type FileUsageEntry,
} from "@/utils/fileUsageReference";

const SECTION_CODE = "Execution_COGS_File";
const WORKSPACE = "Cost of Materials Consumed";
const PROCEDURE = "Predictive Analytics";

const FILE_ROWS = [
  {
    key: "ssr_current",
    label: "Subsequent Sales Register (Current Year)",
  },
  {
    key: "ssr_previous",
    label: "Subsequent Sales Register (Previous Year)",
  },
  {
    key: "purchase_current",
    label: "Purchase Register (Current Year)",
  },
  {
    key: "purchase_previous",
    label: "Purchase Register (Previous Year)",
  },
] as const;

export type PredictiveFileRowKey = (typeof FILE_ROWS)[number]["key"];

interface ExcelColumn {
  name: string;
  value: string;
}

interface RowState {
  selectedFile: string;
  columns: ExcelColumn[];
  loadingColumns: boolean;
  dateColumn: string;
  amountColumn: string;
}

function emptyRow(): RowState {
  return {
    selectedFile: "",
    columns: [],
    loadingColumns: false,
    dateColumn: "",
    amountColumn: "",
  };
}

interface COMCPredictiveAnalyticsProps {
  onBack?: () => void;
}

const INFERENCE_ID = "PREDICTIVE-ANALYTICS-INFERENCE";

interface PredAnalRow {
  Month: string;
  Year?: number;
  Revenue: number;
  Cost: number;
  "Adjusted Cost": number;
  "Predicted Cost"?: number;
  Variance?: number;
}

interface PredAnalJson {
  "Lead Time Input"?: {
    Type?: string;
    Value?: number;
  };
  "Previous Year Analysis"?: {
    "MoM Table"?: PredAnalRow[];
    Correlation?: number;
    "R-Squared"?: number;
    Intercept?: number;
    Slope?: number;
  };
  "Current Year Analysis"?: {
    "MoM Table"?: PredAnalRow[];
    Correlation?: number;
    "R-Squared"?: number;
    "Total Variance"?: number;
    Intercept?: number;
    Slope?: number;
  };
  "Threshold Analysis"?: {
    Population?: number;
    "Performance Materiality"?: number;
    Threshold?: number;
    "Absolute Total Variance"?: number;
    Conclusion?: string;
  };
}

const roundInt = (value: number) => Math.round(Number(value || 0));
const formatInt = (value: number) => roundInt(value).toLocaleString("en-IN");
const formatSignedDecimal = (value: number) => {
  const num = Number(value || 0);
  return `${num >= 0 ? "+" : ""}${num.toFixed(2)}`;
};
const renderMonthTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-white/15 bg-zinc-950/95 px-3 py-2 text-xs shadow-xl">
      <p className="text-white font-semibold">Month: {String(label ?? "-")}</p>
      {payload.map((entry, index) => (
        <p key={`${entry.name}-${index}`} className="text-white/80">
          <span className="text-white/60">{entry.name}:</span> {formatInt(Number(entry.value || 0))}
        </p>
      ))}
    </div>
  );
};

export default function COMCPredictiveAnalytics({ onBack }: COMCPredictiveAnalyticsProps) {
  const [clientFiles, setClientFiles] = useState<Array<{ name: string; reference: string }>>([]);
  const [isLoadingClientFiles, setIsLoadingClientFiles] = useState(false);
  const [referenceValue, setReferenceValue] = useState("");
  const [uploadSlotKey, setUploadSlotKey] = useState<PredictiveFileRowKey | "general" | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);

  const [rows, setRows] = useState<Record<PredictiveFileRowKey, RowState>>(() => {
    const init = {} as Record<PredictiveFileRowKey, RowState>;
    FILE_ROWS.forEach(({ key }) => {
      init[key] = emptyRow();
    });
    return init;
  });

  const [riskLevel, setRiskLevel] = useState<string>("");
  const [controlReliance, setControlReliance] = useState<string>("");
  const [performanceMateriality, setPerformanceMateriality] = useState<string>("");
  const [leadTimeType, setLeadTimeType] = useState<string>("");
  const [leadTimeValue, setLeadTimeValue] = useState<string>("");
  const [runStatus, setRunStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [runMessage, setRunMessage] = useState<string>("");
  const [resultLoading, setResultLoading] = useState(false);
  const [resultError, setResultError] = useState<string>("");
  const [predictiveData, setPredictiveData] = useState<PredAnalJson | null>(null);
  const [isInputSectionOpen, setIsInputSectionOpen] = useState(false);
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  const updateRow = useCallback(
    (key: PredictiveFileRowKey, patch: Partial<RowState>) => {
      setRows((prev) => ({
        ...prev,
        [key]: { ...prev[key], ...patch },
      }));
    },
    [],
  );

  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsageForWorkspace(WORKSPACE, PROCEDURE);
      setFileUsageReference(usage);
    } catch (e) {
      console.error("Error loading file usage reference:", e);
    }
  };

  const loadClientFiles = async () => {
    setIsLoadingClientFiles(true);
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles(SECTION_CODE);
        if (result?.success && result.data?.files) {
          const files = result.data.files.map((f: { name?: string; reference?: string }) => ({
            name: f.name || String(f),
            reference: f.reference || "",
          }));
          setClientFiles(files);
          return;
        }
      }
      setClientFiles([]);
    } catch {
      setClientFiles([]);
    } finally {
      setIsLoadingClientFiles(false);
    }
  };

  useEffect(() => {
    loadClientFiles();
    loadFileUsageReference();
  }, []);

  const persistSlotFile = async (label: string, fileName: string) => {
    const clean = fileName.includes(" (") ? fileName.split(" (")[0] : fileName;
    await updateProcedureFileUsageForWorkspace(WORKSPACE, PROCEDURE, [{ [label]: clean }]);
    await loadFileUsageReference();
  };

  const uploadFileFromDisk = async (slotKey: PredictiveFileRowKey | "general") => {
    setUploadSlotKey(slotKey);
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,.xls,.csv";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) {
        setUploadSlotKey(null);
        return;
      }
      try {
        const base64Content = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(",")[1]);
          };
          reader.onerror = () => reject(new Error("Failed to read file"));
          reader.readAsDataURL(file);
        });

        const fyYear = getFY_YEAR();
        if (!window.sharePointAPI?.uploadFile) {
          setUploadSlotKey(null);
          return;
        }

        const result = await window.sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: file.name,
          referenceValue: referenceValue,
          folderName: "client",
          fyYear: fyYear || undefined,
          sectionList: [SECTION_CODE],
        });

        if (result.success) {
          await loadClientFiles();
          if (slotKey !== "general") {
            onFileChange(slotKey, file.name);
            const row = FILE_ROWS.find((r) => r.key === slotKey);
            if (row) await persistSlotFile(row.label, file.name);
          }
        }
      } catch (err) {
        console.error("Upload failed:", err);
      } finally {
        setUploadSlotKey(null);
      }
    };
    input.click();
  };

  const loadColumnsForRow = async (rowKey: PredictiveFileRowKey) => {
    const selectedFile = rows[rowKey].selectedFile;
    if (!selectedFile) return;

    updateRow(rowKey, { loadingColumns: true });
    try {
      if (window.payroll?.loadExcelColumns) {
        const result = await window.payroll.loadExcelColumns(selectedFile);
        if (result.ok && result.columns) {
          const columns = result.columns.map((col: string) => ({
            name: col,
            value: col,
          }));
          updateRow(rowKey, {
            columns,
            dateColumn: "",
            amountColumn: "",
          });
        }
      } else {
        const testColumns = ["Date", "Amount", "Txn Date", "Value", "Debit", "Credit"];
        updateRow(rowKey, {
          columns: testColumns.map((col) => ({ name: col, value: col })),
          dateColumn: "",
          amountColumn: "",
        });
      }
    } catch {
      updateRow(rowKey, {
        columns: ["Date", "Amount"].map((col) => ({ name: col, value: col })),
        dateColumn: "",
        amountColumn: "",
      });
    } finally {
      updateRow(rowKey, { loadingColumns: false });
    }
  };

  const onFileChange = (rowKey: PredictiveFileRowKey, file: string) => {
    updateRow(rowKey, {
      selectedFile: file,
      columns: [],
      dateColumn: "",
      amountColumn: "",
    });
  };

  const lastUsedForLabel = (label: string) =>
    fileUsageReference.find((e) => e[label])?.[label];

  const loadPredictiveOutput = async () => {
    setResultLoading(true);
    setResultError("");
    try {
      const dl = await window.sharePointAPI?.downloadClientJson?.("Execution_COGS_PredAnal.json");
      if (!dl?.success || !dl.data || typeof dl.data !== "object") {
        throw new Error(dl?.error || "Could not load Execution_COGS_PredAnal.json.");
      }
      setPredictiveData(dl.data as PredAnalJson);
    } catch (error) {
      setResultError(error instanceof Error ? error.message : "Failed to load result JSON.");
      setPredictiveData(null);
    } finally {
      setResultLoading(false);
    }
  };

  useEffect(() => {
    loadPredictiveOutput();
    const loadInferenceQuestion = async () => {
      try {
        const fileName = "Execution_COMC_Responses.json";
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceData = result.data.substantiveProcedures[INFERENCE_ID];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: "linked",
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    const loadCompany = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch {
        setCompanyName("ABC Private Limited");
      }
    };
    loadInferenceQuestion();
    loadCompany();
  }, []);

  const saveInferenceQuestion = async () => {
    try {
      setInferenceSaveStatus("saving");
      setInferenceSaveError(null);
      if (!companyName) {
        throw new Error("Company name not available");
      }
      const fileName = "Execution_COMC_Responses.json";
      let existingData: any = { substantiveProcedures: {} };

      const existingResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
      if (existingResult?.success && existingResult?.data) {
        existingData = existingResult.data;
        if (!existingData.substantiveProcedures) {
          existingData.substantiveProcedures = {};
        }
      }

      let inferenceDocumentNames: string[] = [];
      const sectionToFind = `Execution_COMC_${INFERENCE_ID}`;
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || "")
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${INFERENCE_ID}`);
      const hasInferenceTextboxContent =
        inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;

      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, ""),
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[INFERENCE_ID] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: "Execution COMC Substantive Responses",
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: ["Execution_COMC_Responses"],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus("saved");
      setTimeout(() => setInferenceSaveStatus("idle"), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus("error");
    }
  };

  const runPredictiveAnalytics = async () => {
    try {
      const ssrCurrent = rows.ssr_current;
      const ssrPrevious = rows.ssr_previous;
      const purchaseCurrent = rows.purchase_current;
      const purchasePrevious = rows.purchase_previous;

      const validations = [
        ssrCurrent.selectedFile && ssrCurrent.dateColumn && ssrCurrent.amountColumn,
        ssrPrevious.selectedFile && ssrPrevious.dateColumn && ssrPrevious.amountColumn,
        purchaseCurrent.selectedFile && purchaseCurrent.dateColumn && purchaseCurrent.amountColumn,
        purchasePrevious.selectedFile && purchasePrevious.dateColumn && purchasePrevious.amountColumn,
        riskLevel,
        controlReliance,
        performanceMateriality,
        leadTimeType,
        leadTimeValue,
      ];
      if (validations.some((v) => !v)) {
        setRunStatus("error");
        setRunMessage("Please complete all file mappings and parameter inputs before running.");
        return;
      }

      setRunStatus("running");
      setRunMessage("");

      const result = await window.payroll.run("execute_comc_predictive_analytics", {
        inputFiles: [],
        options: {
          folder_name: "client",
          files_map: {
            PY_SALES_FILE: ssrPrevious.selectedFile,
            PY_PURCHASE_FILE: purchasePrevious.selectedFile,
            CY_SALES_FILE: ssrCurrent.selectedFile,
            CY_PURCHASE_FILE: purchaseCurrent.selectedFile,
          },
          PY_SALES_DATE_COLUMN: ssrPrevious.dateColumn,
          PY_SALES_AMOUNT_COLUMN: ssrPrevious.amountColumn,
          PY_PURCHASE_DATE_COLUMN: purchasePrevious.dateColumn,
          PY_PURCHASE_AMOUNT_COLUMN: purchasePrevious.amountColumn,
          CY_SALES_DATE_COLUMN: ssrCurrent.dateColumn,
          CY_SALES_AMOUNT_COLUMN: ssrCurrent.amountColumn,
          CY_PURCHASE_DATE_COLUMN: purchaseCurrent.dateColumn,
          CY_PURCHASE_AMOUNT_COLUMN: purchaseCurrent.amountColumn,
          RISK_LEVEL: riskLevel,
          CONTROL_RELIANCE: controlReliance,
          PERFORMANCE_MATERIALITY: Number(performanceMateriality),
          LEAD_TIME_TYPE: leadTimeType,
          LEAD_TIME_VALUE: Number(leadTimeValue),
        },
      });

      if (result?.ok) {
        setRunStatus("completed");
        setRunMessage("Execution completed");
        await loadPredictiveOutput();
      } else {
        setRunStatus("error");
        setRunMessage(result?.error || "Failed to run predictive analytics.");
      }
    } catch (error) {
      setRunStatus("error");
      setRunMessage(error instanceof Error ? error.message : "Failed to run predictive analytics.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Predictive Analytics</h2>
          <p className="text-gray-400">
            Cost of Materials Consumed — select source files, map columns, and set parameters.
          </p>
          <p className="mt-1 text-xs text-white/50">
            Files use section code{" "}
            <span className="font-mono text-white/70">{SECTION_CODE}</span> (uploads are tagged the same).
          </p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack} className="shrink-0">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        )}
      </div>

      <Card
        className="border-white/10 bg-black/30 cursor-pointer"
        onClick={() => setIsInputSectionOpen((prev) => !prev)}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <LineChart className="h-5 w-5 text-violet-400" />
              Inputs
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                setIsInputSectionOpen((prev) => !prev);
              }}
              className="text-white hover:bg-white/10"
              aria-label={isInputSectionOpen ? "Collapse input section" : "Expand input section"}
            >
              <ChevronDown
                className={`h-5 w-5 transition-transform ${isInputSectionOpen ? "rotate-180" : "rotate-0"}`}
              />
            </Button>
          </div>
          <CardDescription className="text-white/55">
            Expand to select source files, map columns, configure parameters, and run predictive analytics.
          </CardDescription>
        </CardHeader>
      </Card>

      {isInputSectionOpen && (
        <div className="rounded-xl border border-white/10 bg-black/30 p-3 sm:p-5 space-y-6">
      {/* Select Source Files — same layout as Headcount Reconciliation */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-white">
            <LineChart className="h-5 w-5 text-violet-400" />
            Select Source Files
          </CardTitle>
          <CardDescription className="text-white/55">
            Choose one file per register from documents tagged{" "}
            <span className="font-mono text-white/70">{SECTION_CODE}</span>, or upload a file for that slot (uploads use
            the same section code).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => loadClientFiles()}
              disabled={isLoadingClientFiles}
              className="border-white/10 bg-white/5 text-white hover:bg-white/10"
            >
              {isLoadingClientFiles ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading…
                </>
              ) : (
                "Refresh file list"
              )}
            </Button>
            {clientFiles.length > 0 && (
              <span className="text-sm text-emerald-400/90">{clientFiles.length} file(s) available</span>
            )}
          </div>

          {isLoadingClientFiles && clientFiles.length === 0 && (
            <p className="text-sm text-white/50">Loading files from SharePoint…</p>
          )}

          {!isLoadingClientFiles && clientFiles.length === 0 && (
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4 space-y-3">
              <p className="text-sm text-amber-200/90">
                No files found for this section yet. Add a reference (optional), upload a file — it will be stored with{" "}
                <span className="font-mono">{SECTION_CODE}</span> — then refresh and assign it to a register below.
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="space-y-1.5 flex-1 min-w-[200px]">
                  <Label className="text-white/80 text-xs">Reference (optional)</Label>
                  <Input
                    value={referenceValue}
                    onChange={(e) => setReferenceValue(e.target.value)}
                    placeholder="Reference value"
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
                <Button
                  type="button"
                  onClick={() => uploadFileFromDisk("general")}
                  disabled={uploadSlotKey !== null}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {uploadSlotKey === "general" ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload file
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {clientFiles.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FILE_ROWS.map(({ key, label }) => (
                <div key={key} className="space-y-2">
                  <Label className="text-white">{label}</Label>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Select
                      value={rows[key].selectedFile || undefined}
                      onValueChange={async (value) => {
                        onFileChange(key, value);
                        await persistSlotFile(label, value);
                      }}
                    >
                      <SelectTrigger className="border-white/10 bg-black/50 text-white flex-1 min-w-0">
                        <SelectValue placeholder="Select…" />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-black/90 text-white max-h-64">
                        {clientFiles.map((f, i) => (
                          <SelectItem key={`${f.name}-${i}`} value={f.name}>
                            {f.name}
                            {f.reference ? ` (${f.reference})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0 border-white/15 text-white hover:bg-white/10"
                      disabled={uploadSlotKey !== null}
                      onClick={() => uploadFileFromDisk(key)}
                    >
                      {uploadSlotKey === key ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Upload className="mr-1.5 h-3.5 w-3.5" />
                          Upload
                        </>
                      )}
                    </Button>
                  </div>
                  {lastUsedForLabel(label) && (
                    <p className="text-xs text-white/50">Last used: {lastUsedForLabel(label)}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Map columns — mirrors Headcount &quot;Map Date Columns&quot; pattern */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">Map Date &amp; Amount columns</CardTitle>
          <CardDescription className="text-white/55">
            For each register, load columns from the selected file, then map <strong className="text-white/80">Date</strong>{" "}
            and <strong className="text-white/80">Amount</strong>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {FILE_ROWS.map(({ key, label }) => {
            const r = rows[key];
            const colsReady = r.columns.length > 0;
            return (
              <div
                key={key}
                className="rounded-lg border border-white/10 bg-white/[0.03] p-4 space-y-3"
              >
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm font-medium text-white">{label}</p>
                  <p className="text-xs text-white/45 truncate" title={r.selectedFile || undefined}>
                    File: {r.selectedFile || "—"}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    disabled={!r.selectedFile || r.loadingColumns}
                    onClick={() => loadColumnsForRow(key)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {r.loadingColumns ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading…
                      </>
                    ) : (
                      "Load columns"
                    )}
                  </Button>
                  {colsReady && (
                    <span className="text-xs text-emerald-400/90">{r.columns.length} columns loaded</span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-white/90">Date</Label>
                    <Select
                      value={r.dateColumn || undefined}
                      onValueChange={(v) => updateRow(key, { dateColumn: v })}
                      disabled={!colsReady}
                    >
                      <SelectTrigger className="border-white/10 bg-black/50 text-white">
                        <SelectValue placeholder={colsReady ? "Select column" : "Load columns first"} />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-zinc-950 text-white max-h-60">
                        {r.columns.map((col) => (
                          <SelectItem key={`${key}-d-${col.value}`} value={col.value}>
                            {col.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/90">Amount</Label>
                    <Select
                      value={r.amountColumn || undefined}
                      onValueChange={(v) => updateRow(key, { amountColumn: v })}
                      disabled={!colsReady}
                    >
                      <SelectTrigger className="border-white/10 bg-black/50 text-white">
                        <SelectValue placeholder={colsReady ? "Select column" : "Load columns first"} />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-zinc-950 text-white max-h-60">
                        {r.columns.map((col) => (
                          <SelectItem key={`${key}-a-${col.value}`} value={col.value}>
                            {col.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white">Parameters</CardTitle>
          <CardDescription className="text-white/55">
            Risk, controls, materiality, and lead time used for the predictive procedure.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2">
              <Label className="text-white/90">Risk level</Label>
              <Select value={riskLevel || undefined} onValueChange={setRiskLevel}>
                <SelectTrigger className="border-white/10 bg-black/50 text-white">
                  <SelectValue placeholder="Select risk level" />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-zinc-950 text-white">
                  <SelectItem value="Lower">Lower</SelectItem>
                  <SelectItem value="Higher">Higher</SelectItem>
                  <SelectItem value="Significant">Significant</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-white/90">Control reliance</Label>
              <Select value={controlReliance || undefined} onValueChange={setControlReliance}>
                <SelectTrigger className="border-white/10 bg-black/50 text-white">
                  <SelectValue placeholder="Select reliance" />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-zinc-950 text-white">
                  <SelectItem value="Relying on Controls">Relying on Controls</SelectItem>
                  <SelectItem value="Not Relying on Controls">Not Relying on Controls</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 sm:col-span-2 lg:col-span-1">
              <Label className="text-white/90">Performance materiality</Label>
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                value={performanceMateriality}
                onChange={(e) => setPerformanceMateriality(e.target.value)}
                placeholder="Integer"
                className="border-white/10 bg-black/50 text-white"
              />
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-white/10 bg-white/[0.03] p-4">
            <Label className="text-white/90 mb-3 block">Lead time</Label>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
              <div className="space-y-2 flex-1 min-w-[160px]">
                <span className="text-xs text-white/50">Type</span>
                <Select value={leadTimeType || undefined} onValueChange={setLeadTimeType}>
                  <SelectTrigger className="border-white/10 bg-black/50 text-white">
                    <SelectValue placeholder="Days or weeks" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-zinc-950 text-white">
                    <SelectItem value="Days">Days</SelectItem>
                    <SelectItem value="Weeks">Weeks</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 flex-1 min-w-[160px]">
                <span className="text-xs text-white/50">Value</span>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  step={1}
                  value={leadTimeValue}
                  onChange={(e) => setLeadTimeValue(e.target.value)}
                  placeholder="Integer"
                  className="border-white/10 bg-black/50 text-white"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-white">
            <Play className="h-5 w-5 text-emerald-400" />
            Run Predictive Analytics
          </CardTitle>
          <CardDescription className="text-white/55">
            Runs the Python engine and uploads `Execution_COGS_PredAnal.json` to the juggernaut folder.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            type="button"
            onClick={runPredictiveAnalytics}
            disabled={runStatus === "running"}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {runStatus === "running" ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Run
              </>
            )}
          </Button>

          {runStatus === "completed" && (
            <p className="text-sm text-emerald-400 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              {runMessage}
            </p>
          )}
          {runStatus === "error" && (
            <p className="text-sm text-red-400 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {runMessage}
            </p>
          )}
        </CardContent>
      </Card>
      </div>
      )}

      {resultLoading && (
        <Card className="border-white/10 bg-black/40">
          <CardContent className="py-6 text-sm text-white/70 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading predictive analytics output...
          </CardContent>
        </Card>
      )}

      {resultError && (
        <Card className="border-red-500/20 bg-red-500/10">
          <CardContent className="py-4 text-sm text-red-300">{resultError}</CardContent>
        </Card>
      )}

      {predictiveData && (
        <div className="space-y-6">
          <Card className="border-white/10 bg-black/40">
            <CardHeader>
              <CardTitle className="text-white">Current Year Analysis</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="overflow-auto rounded-lg border border-white/10">
                <table className="w-full text-sm">
                  {(() => {
                    const currentYearRows = predictiveData["Current Year Analysis"]?.["MoM Table"] || [];
                    const totals = currentYearRows.reduce(
                      (acc, row) => {
                        acc.revenue += Number(row.Revenue || 0);
                        acc.cost += Number(row.Cost || 0);
                        acc.adjustedCost += Number(row["Adjusted Cost"] || 0);
                        acc.predictedCost += Number(row["Predicted Cost"] || 0);
                        acc.variance += Number(row.Variance || 0);
                        return acc;
                      },
                      { revenue: 0, cost: 0, adjustedCost: 0, predictedCost: 0, variance: 0 },
                    );
                    return (
                      <>
                  <thead className="bg-white/5 text-white/70">
                    <tr>
                      <th className="px-3 py-2 text-left">Month</th>
                      <th className="px-3 py-2 text-right">Revenue</th>
                      <th className="px-3 py-2 text-right">Cost</th>
                      <th className="px-3 py-2 text-right">Adjusted Cost</th>
                      <th className="px-3 py-2 text-right">Predicted Cost</th>
                      <th className="px-3 py-2 text-right">Variance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(predictiveData["Current Year Analysis"]?.["MoM Table"] || []).map((row) => (
                      <tr key={row.Month} className="border-t border-white/10">
                        <td className="px-3 py-2 text-white">{row.Month}</td>
                        <td className="px-3 py-2 text-right text-white/90">{formatInt(Number(row.Revenue || 0))}</td>
                        <td className="px-3 py-2 text-right text-white/90">{formatInt(Number(row.Cost || 0))}</td>
                        <td className="px-3 py-2 text-right text-white/90">{formatInt(Number(row["Adjusted Cost"] || 0))}</td>
                        <td className="px-3 py-2 text-right text-white/90">{formatInt(Number(row["Predicted Cost"] || 0))}</td>
                        <td className="px-3 py-2 text-right text-white/90">{formatInt(Number(row.Variance || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-white/20 bg-white/[0.04]">
                      <td className="px-3 py-2 text-white font-semibold">Total</td>
                      <td className="px-3 py-2 text-right text-white font-semibold">{formatInt(totals.revenue)}</td>
                      <td className="px-3 py-2 text-right text-white font-semibold">{formatInt(totals.cost)}</td>
                      <td className="px-3 py-2 text-right text-white font-semibold">{formatInt(totals.adjustedCost)}</td>
                      <td className="px-3 py-2 text-right text-white font-semibold">{formatInt(totals.predictedCost)}</td>
                      <td className="px-3 py-2 text-right text-white font-semibold">{formatInt(totals.variance)}</td>
                    </tr>
                  </tfoot>
                      </>
                    );
                  })()}
                </table>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="h-72 rounded-lg border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] p-4">
                  <div className="h-full flex flex-col">
                    <div className="flex items-center justify-between">
                      <h4 className="text-white font-semibold">Regression Analysis</h4>
                      <span className="text-[11px] px-2 py-1 rounded-full border border-cyan-400/30 bg-cyan-400/10 text-cyan-300">
                        Current Year
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <div className="rounded-md border border-white/10 bg-black/25 px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-white/55">Correlation</p>
                        <p className="mt-1 text-lg font-bold text-white">
                          {predictiveData["Current Year Analysis"]?.Correlation != null
                            ? formatSignedDecimal(predictiveData["Current Year Analysis"]?.Correlation || 0)
                            : "-"}
                        </p>
                      </div>
                      <div className="rounded-md border border-white/10 bg-black/25 px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-white/55">Slope</p>
                        <p className="mt-1 text-lg font-semibold text-white">
                          {formatSignedDecimal(predictiveData["Current Year Analysis"]?.Slope || 0)}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 rounded-md border border-white/10 bg-black/25 p-3">
                      <p className="text-[11px] uppercase tracking-wide text-white/55">Regression Equation</p>
                      <p className="mt-1 text-sm text-white/90">
                        Predicted Cost = {formatInt(predictiveData["Current Year Analysis"]?.Intercept || 0)}{" "}
                        {formatSignedDecimal(predictiveData["Current Year Analysis"]?.Slope || 0)} x Revenue
                      </p>
                      <p className="mt-2 text-xs text-white/60">
                        Intercept: {formatInt(predictiveData["Current Year Analysis"]?.Intercept || 0)}
                      </p>
                    </div>

                    <p className="mt-auto text-[11px] text-white/45">
                      Values are rounded for display; signs indicate direction.
                    </p>
                  </div>
                </div>
                <div className="h-72 rounded-lg border border-white/10 bg-white/[0.03] p-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsLineChart data={predictiveData["Current Year Analysis"]?.["MoM Table"] || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="Month" tick={{ fill: "#cbd5e1", fontSize: 11 }} />
                      <YAxis tick={{ fill: "#cbd5e1", fontSize: 11 }} tickFormatter={(v) => formatInt(Number(v))} />
                      <Tooltip content={renderMonthTooltip} />
                      <Legend />
                      <Line type="monotone" dataKey="Adjusted Cost" stroke="#22d3ee" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="Predicted Cost" stroke="#f472b6" strokeWidth={2} dot={false} />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-black/40">
            <CardHeader>
              <CardTitle className="text-white">Threshold Analysis</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="overflow-auto rounded-lg border border-white/10">
                  <table className="w-full text-sm">
                    <tbody>
                      {Object.entries(predictiveData["Threshold Analysis"] || {}).map(([k, v]) => (
                        <tr key={k} className="border-t border-white/10">
                          <td className="px-3 py-2 text-white/80">{k}</td>
                          <td
                            className={`px-3 py-2 text-right ${
                              k === "Conclusion"
                                ? String(v).toLowerCase().includes("within")
                                  ? "text-emerald-400"
                                  : "text-red-400"
                                : "text-white"
                            }`}
                          >
                            {typeof v === "number" ? formatInt(v) : String(v)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                  <p className="text-[11px] uppercase tracking-wide text-white/55">Lead Time Input</p>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div className="rounded-md border border-white/10 bg-black/25 px-3 py-2">
                      <p className="text-[11px] uppercase tracking-wide text-white/55">Type</p>
                      <p className="mt-1 text-lg font-semibold text-white">
                        {predictiveData["Lead Time Input"]?.Type || "-"}
                      </p>
                    </div>
                    <div className="rounded-md border border-white/10 bg-black/25 px-3 py-2">
                      <p className="text-[11px] uppercase tracking-wide text-white/55">Value</p>
                      <p className="mt-1 text-lg font-semibold text-white">
                        {predictiveData["Lead Time Input"]?.Value != null
                          ? formatInt(Number(predictiveData["Lead Time Input"]?.Value || 0))
                          : "-"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="h-72 rounded-lg border border-white/10 bg-white/[0.03] p-3">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      {
                        cluster: "Population vs Total Predicted Cost (CY)",
                        a: predictiveData["Threshold Analysis"]?.Population || 0,
                        b: (predictiveData["Current Year Analysis"]?.["MoM Table"] || []).reduce(
                          (s, r) => s + Number(r["Predicted Cost"] || 0),
                          0,
                        ),
                      },
                      {
                        cluster: "Threshold vs Absolute Total Variance",
                        a: predictiveData["Threshold Analysis"]?.Threshold || 0,
                        b: predictiveData["Threshold Analysis"]?.["Absolute Total Variance"] || 0,
                      },
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="cluster" tick={{ fill: "#cbd5e1", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#cbd5e1", fontSize: 11 }} tickFormatter={(v) => formatInt(Number(v))} />
                    <Tooltip formatter={(value: number) => formatInt(Number(value))} />
                    <Legend />
                    <Bar dataKey="a" fill="#38bdf8" name="Metric A" />
                    <Bar dataKey="b" fill="#a78bfa" name="Metric B" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-black/40">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">
                  Document the inference and conclusion from the above procedure performed.
                </CardTitle>
                <div className="flex items-center gap-2">
                  {inferenceSaveStatus === "saved" && (
                    <span className="text-sm text-green-400">Saved!</span>
                  )}
                  {inferenceSaveStatus === "error" && inferenceSaveError && (
                    <span className="text-sm text-red-400">Error</span>
                  )}
                  <Button
                    onClick={saveInferenceQuestion}
                    disabled={inferenceSaveStatus === "saving" || !companyName}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                  >
                    {inferenceSaveStatus === "saving" ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <DocumentManager
                sectionKey="comc"
                itemId={INFERENCE_ID}
                itemQuestion="Document the inference and conclusion from the above procedure performed."
                itemTextResponse={inferenceQuestion.textResponse}
                itemDocuments={inferenceQuestion.documents || []}
                onTextResponseChange={(textResponse) =>
                  setInferenceQuestion({ ...inferenceQuestion, textResponse })
                }
                onDocumentsChange={(documents) =>
                  setInferenceQuestion({ ...inferenceQuestion, documents })
                }
                disabled={false}
              />
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-black/40">
            <CardHeader>
              <CardTitle className="text-white">Previous Year Analysis</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="overflow-auto rounded-lg border border-white/10">
                <table className="w-full text-sm">
                  {(() => {
                    const previousYearRows = predictiveData["Previous Year Analysis"]?.["MoM Table"] || [];
                    const totals = previousYearRows.reduce(
                      (acc, row) => {
                        acc.revenue += Number(row.Revenue || 0);
                        acc.cost += Number(row.Cost || 0);
                        acc.adjustedCost += Number(row["Adjusted Cost"] || 0);
                        return acc;
                      },
                      { revenue: 0, cost: 0, adjustedCost: 0 },
                    );
                    return (
                      <>
                  <thead className="bg-white/5 text-white/70">
                    <tr>
                      <th className="px-3 py-2 text-left">Month</th>
                      <th className="px-3 py-2 text-right">Revenue</th>
                      <th className="px-3 py-2 text-right">Cost</th>
                      <th className="px-3 py-2 text-right">Adjusted Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(predictiveData["Previous Year Analysis"]?.["MoM Table"] || []).map((row) => (
                      <tr key={row.Month} className="border-t border-white/10">
                        <td className="px-3 py-2 text-white">{row.Month}</td>
                        <td className="px-3 py-2 text-right text-white/90">{formatInt(Number(row.Revenue || 0))}</td>
                        <td className="px-3 py-2 text-right text-white/90">{formatInt(Number(row.Cost || 0))}</td>
                        <td className="px-3 py-2 text-right text-white/90">{formatInt(Number(row["Adjusted Cost"] || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-white/20 bg-white/[0.04]">
                      <td className="px-3 py-2 text-white font-semibold">Total</td>
                      <td className="px-3 py-2 text-right text-white font-semibold">{formatInt(totals.revenue)}</td>
                      <td className="px-3 py-2 text-right text-white font-semibold">{formatInt(totals.cost)}</td>
                      <td className="px-3 py-2 text-right text-white font-semibold">{formatInt(totals.adjustedCost)}</td>
                    </tr>
                  </tfoot>
                      </>
                    );
                  })()}
                </table>
              </div>
              <div className="space-y-4">
                <p className="text-sm text-white/80">
                  Correlation:{" "}
                  <span className="text-white font-bold">
                    {predictiveData["Previous Year Analysis"]?.Correlation != null
                      ? formatSignedDecimal(predictiveData["Previous Year Analysis"]?.Correlation || 0)
                      : "-"}
                  </span>
                </p>
                <div className="h-72 rounded-lg border border-white/10 bg-white/[0.03] p-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 8, right: 12, bottom: 24, left: 56 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis
                        dataKey="Revenue"
                        name="Revenue"
                        label={{
                          value: "Revenue",
                          position: "insideBottom",
                          offset: -4,
                          fill: "#cbd5e1",
                          fontSize: 11,
                        }}
                        tick={{ fill: "#cbd5e1", fontSize: 11 }}
                        tickFormatter={(v) => formatInt(Number(v))}
                      />
                      <YAxis
                        dataKey="Adjusted Cost"
                        name="Adjusted Cost"
                        label={{
                          value: "Adjusted Cost",
                          angle: -90,
                          position: "left",
                          dx: -20,
                          fill: "#cbd5e1",
                          fontSize: 11,
                        }}
                        width={100}
                        tickMargin={10}
                        tick={{ fill: "#cbd5e1", fontSize: 11 }}
                        tickFormatter={(v) => formatInt(Number(v))}
                      />
                      <Tooltip
                        cursor={{ strokeDasharray: "3 3" }}
                        formatter={(value: number) => formatInt(Number(value))}
                      />
                      <Scatter
                        name="Adjusted Cost vs Revenue"
                        data={predictiveData["Previous Year Analysis"]?.["MoM Table"] || []}
                        fill="#22d3ee"
                        line={{ stroke: "#93c5fd", strokeWidth: 2, strokeDasharray: "5 5" }}
                        lineType="joint"
                      />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
